import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../table";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { RouterLink as Link } from "../../router";
import type { DbTableDetailDto } from "../../api/contracts/database/schema";

/**
 * Per-object detail — columns (+PK/NOT NULL/default + Meaning), foreign keys,
 * indexes, triggers, and the verbatim CREATE DDL from sqlite_master.
 * Server-renderable; pass `detail` from tableDetail(). The optional `meaning`
 * (table-level) + `columnMeanings` (per-column) come from the semantic registry
 * (Data Design §5.4) so one glance = structure + semantics.
 */
export function ObjectDetail({
  detail,
  meaning,
  columnMeanings,
}: {
  detail: DbTableDetailDto;
  meaning?: string | null;
  columnMeanings?: Record<string, string>;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{detail.type}</Badge>
        <Badge variant="outline">{detail.group}</Badge>
        {detail.system ? <Badge variant="outline">system</Badge> : null}
        <span className="type-body text-muted-foreground">
          {detail.rowCount < 0 ? "—" : detail.rowCount.toLocaleString()} rows
        </span>
        {detail.type === "table" && !detail.system ? (
          <Button asChild variant="outline" size="sm" className="ml-auto">
            <Link href={`/database/editor/${encodeURIComponent(detail.name)}`}>
              Open in Table Editor
            </Link>
          </Button>
        ) : null}
      </div>

      {meaning ? (
        <p className="type-body text-muted-foreground">{meaning}</p>
      ) : null}

      <Card className="gap-0 overflow-hidden border border-border py-0">
        <CardHeader className="px-4 py-3">
          <CardTitle className="type-title">Columns</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Not null</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>PK</TableHead>
                <TableHead>Meaning</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.columns.map((c) => (
                <TableRow key={c.name}>
                  <TableCell className="text-foreground">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">{c.type || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{c.notnull ? "yes" : ""}</TableCell>
                  <TableCell className="text-muted-foreground">{c.dflt_value ?? ""}</TableCell>
                  <TableCell className="text-muted-foreground">{c.pk > 0 ? c.pk : ""}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {columnMeanings?.[c.name] ?? ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {detail.foreignKeys.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Foreign keys</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1.5">
            {detail.foreignKeys.map((fk, i) => (
              <div key={i} className="flex items-center gap-2 type-body">
                <span className="text-foreground">{fk.from}</span>
                <span className="text-muted-foreground">→</span>
                <Link
                  href={`/database/schema/${encodeURIComponent(fk.table)}`}
                  className="text-foreground underline decoration-dotted underline-offset-2"
                >
                  {fk.table}.{fk.to}
                </Link>
                <span className="type-subtext text-muted-foreground">
                  on update {fk.on_update || "NO ACTION"} · on delete {fk.on_delete || "NO ACTION"}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {detail.indexes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Indexes</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1.5">
            {detail.indexes.map((idx) => (
              <div key={idx.name} className="flex items-center gap-2 type-body">
                <span className="text-foreground">{idx.name}</span>
                {idx.unique ? <Badge variant="outline">unique</Badge> : null}
                <span className="text-muted-foreground">({idx.columns.join(", ")})</span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {detail.triggers.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="type-title">Triggers</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {detail.triggers.map((t) => (
              <div key={t.name} className="flex flex-col gap-1">
                <span className="type-body text-foreground">{t.name}</span>
                <pre className="overflow-x-auto rounded-md bg-surface-3 p-2 font-mono type-subtext text-muted-foreground">
                  {t.sql}
                </pre>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="type-title">CREATE statement</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-md bg-surface-3 p-3 font-mono type-subtext text-foreground">
            {detail.ddl || "(no DDL recorded)"}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
